import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  InteractionSearchRequest,
  InteractionListRequest,
  InteractionChannel,
} from '@vagusx/clients';
import { getInteractionClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';

export interface PatientTimelineRequest {
  limit?: number;
  offset?: number;
  channel?: InteractionChannel;
  startDate?: string;
  endDate?: string;
}

/**
 * Hook to fetch and search interactions with metadata (Activity view).
 */
export function useInteractions(request?: InteractionListRequest) {
  return useQuery({
    queryKey: queryKeys.interactions.list(request),
    queryFn: () => getInteractionClient().list(request),
    enabled: request !== undefined,
  });
}

/**
 * Hook to fetch patient timeline (all interactions for a specific patient).
 */
export function usePatientTimeline(
  patientId: string | undefined,
  request?: PatientTimelineRequest
) {
  return useQuery({
    queryKey: queryKeys.interactions.patientTimeline(
      patientId ?? '',
      request as Record<string, unknown> | undefined
    ),
    queryFn: () => getInteractionClient().getPatientTimeline(patientId!, request),
    enabled: !!patientId,
  });
}

/**
 * Hook to search interactions (legacy inbox view).
 */
export function useSearchInteractions(searchRequest?: InteractionSearchRequest) {
  return useQuery({
    queryKey: [...queryKeys.interactions.all, 'search', searchRequest] as const,
    queryFn: () => getInteractionClient().search(searchRequest ?? {}),
  });
}

/**
 * Hook to fetch a single interaction by ID.
 */
export function useInteraction(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.interactions.detail(id ?? ''),
    queryFn: () => getInteractionClient().getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch interaction messages (transcript).
 */
export function useInteractionMessages(interactionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.interactions.messages(interactionId ?? ''),
    queryFn: () => getInteractionClient().getMessages(interactionId!),
    enabled: !!interactionId,
  });
}

/**
 * Hook to fetch the post-call LLM evaluation for an interaction.
 * Returns `null` when no evaluation row exists yet (e.g. inbound calls or
 * calls not yet evaluated). The `improvements` field is stripped by
 * the API for non-system-admins.
 */
export function useInteractionEvaluation(interactionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.interactions.evaluation(interactionId ?? ''),
    queryFn: () => getInteractionClient().getEvaluation(interactionId!),
    enabled: !!interactionId,
  });
}

/**
 * Hook to fetch interactions for a specific task.
 * Note: This uses searchTerm as a workaround until proper task filtering is added to the API.
 */
export function useTaskInteractions(taskId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.interactions.all, 'task', taskId] as const,
    queryFn: () => getInteractionClient().search({ searchTerm: taskId }),
    enabled: !!taskId,
  });
}

/**
 * Hook to mint a short-TTL signed URL for a call recording. Disabled
 * until `enabled` is true so the network call only fires the moment the
 * user clicks Play (we don't want to burn a signature every time the
 * sheet opens — opening with autoplay-off doesn't need a URL).
 *
 * The endpoint signs for 30 minutes; we set `staleTime` to 25 minutes
 * so refetching mid-listen reuses the same URL instead of hitting the
 * API on every component re-render. The 5-minute safety margin covers
 * clock skew + the user pausing for a bit before resuming.
 */
export function useRecordingUrl(interactionId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.interactions.recordingUrl(interactionId ?? ''),
    queryFn: () => getInteractionClient().getRecordingUrl(interactionId!),
    enabled: !!interactionId && enabled,
    staleTime: 25 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
  });
}

/**
 * Hook to fetch interaction timeline events.
 */
export function useInteractionTimeline(interactionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.interactions.timeline(interactionId ?? ''),
    queryFn: () => getInteractionClient().getTimeline(interactionId!),
    enabled: !!interactionId,
  });
}

/**
 * Hook to flag an interaction (also auto-flags the patient).
 */
export function useFlagInteraction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => getInteractionClient().flagInteraction(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.interactions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.interactions.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.all });
    },
  });
}

/**
 * Hook to unflag an interaction (does NOT auto-unflag the patient).
 */
export function useUnflagInteraction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => getInteractionClient().unflagInteraction(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.interactions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.interactions.detail(id) });
    },
  });
}
